import {
  Text,
  type TextProps,
  Tooltip,
  type TooltipProps,
} from "@mantine/core";
import { useMemo } from "react";
import { twMerge } from "tailwind-merge";

import type { Nullable } from "@/types/utils";
import { truncateString } from "@/utils/helpers";

export interface TruncatedTextProps {
  maxLength?: number;
  textElProps?: Partial<TextProps>;
  tooltipProps?: Partial<TooltipProps>;
  // Some feeding columns are nullable in the database (SRN-803): render an
  // empty element rather than letting a missing value take the page down.
  value: Nullable<string>;
}

export const TruncatedText = ({
  maxLength = 50,
  textElProps = {},
  tooltipProps = {},
  value,
}: TruncatedTextProps) => {
  const text = value ?? "";
  const shouldTruncate = useMemo(
    () => text.length > maxLength,
    [maxLength, text],
  );
  const textEl = useMemo(() => {
    // -3 to accommodate ellipsis
    const displayValue = shouldTruncate
      ? truncateString(text, maxLength - 3)
      : text;
    const { className: propsClassName = "", ...otherProps } = textElProps;
    const className = twMerge([shouldTruncate && "truncate", propsClassName]);

    return (
      <Text className={className} {...otherProps}>
        {displayValue}
      </Text>
    );
  }, [maxLength, shouldTruncate, textElProps, text]);

  if (shouldTruncate) {
    return (
      <Tooltip
        label={text}
        withArrow
        arrowSize={8}
        c="black"
        color="white"
        className="border border-gray-200 z-[1000]"
        {...tooltipProps}
      >
        {textEl}
      </Tooltip>
    );
  }

  return textEl;
};
